import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, isAdminRole, type Me } from "./api";
import { sellingLive } from "./billing";
import { Logo } from "./Logo";
import { Icon } from "./Icon";
import { navigate, usePath, useRecipientIds, useReplyTarget } from "./router";
import { PeoplePanel, type MessageScope } from "./PeoplePanel";
import { THEMES, useTheme } from "./theme";
import { LANGS, LOCALES, errorText, langName, useLang, useT } from "./i18n";
import { Failed, Loading } from "./Loading";
import { SessionProvider, useSession } from "./session";
import { Login } from "./pages/Login";
import { RecoverAccess } from './pages/RecoverAccess';
import { Onboarding } from "./pages/Onboarding";
import { Restore } from "./pages/Restore";
import { Compose } from "./pages/Compose";
import { TryIt } from "./pages/TryIt";
import { Capsules } from "./pages/CapsulesPage";
import { Correspondence } from "./pages/Correspondence";
import { Admin } from "./pages/Admin";
import { Account } from "./pages/Account";
import { KeyBar } from "./pages/Unlock";
import { Guide } from "./pages/Guide";
import { VerifyEmail } from "./pages/VerifyEmail";
import { Unsubscribe } from "./pages/Unsubscribe";
import { Contacts } from "./pages/Contacts";
import { Terms } from "./pages/Terms";
import { LegalPage } from "./pages/Legal";
import { Following } from "./pages/Following";
import { Connect } from "./pages/Connect";
import { Developers } from "./pages/Developers";
import { Billing } from "./pages/Billing";
import { Pricing } from "./pages/Pricing";
import { Keys } from "./pages/Keys";
import { ReauthDone } from "./ReauthDialog";
import { initials } from "./PersonMenu";
import { TopSearch } from "./TopSearch";
import { SealedNotice } from "./SealedNotice";
import { Search } from "./pages/Search";
import { RefreshButton, RefreshProvider, useRefreshHandler } from "./Refresh";

export function App() {
  return <RefreshProvider><AppContent /></RefreshProvider>;
}

function AppContent() {
  const t = useT();
  const path = usePath();
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [suspended,setSuspended]=useState(false);
  useEffect(()=>{const block=()=>setSuspended(true);window.addEventListener('vrelis:suspended',block);return()=>window.removeEventListener('vrelis:suspended',block);},[]);
  // A signed-out page can detect a sign-in from another tab without clearing its form inputs.
  useRefreshHandler(async () => { if (!me) { const fresh = await api.me(); setError(null); setMe(fresh); } });

  useEffect(() => {
    api.me().then(setMe, (err: unknown) => setError(errorText(err)));
  }, []);

  // The guide is open to everyone, logged in or not (an invite can link to it).
  if (path === "/guide") return <Guide />;
  if (path === "/terms") return <Terms />;
  if (path === "/privacy") return <LegalPage page="privacy" />;
  if (path === "/cookies") return <LegalPage page="cookies" />;
  if (path === "/contact") return <LegalPage page="contact" />;
  if (path === "/developers") return <Developers />;
  if (path === "/pricing") return <Pricing />;
  // Mail links work whether or not this browser is signed in (PLAN.md 4.14).
  if (path === "/verify") return <VerifyEmail />;
  if (path === "/unsubscribe") return <Unsubscribe />;
  if (path === '/recover-access') return <RecoverAccess/>;
  if (path === "/reauth-done") return <ReauthDone />;
  if(suspended) return <><PublicHeader/><main className="narrow"><p className="error" role="alert">{t.login.errors.member_suspended}</p><p><a href={`mailto:${PROBLEM_ADDRESS}`}>{PROBLEM_ADDRESS}</a></p><button onClick={()=>void api.logout().then(()=>{location.href='/';})}>{t.account.logout}</button></main><Footer/></>;
  if (error) return <Failed text={t.app.cannotReach(error)} />;
  if (me === undefined) return <Loading />;
  if (me === null) return <Login />;
  return (
    <SessionProvider me={me}>
      <Shell />
    </SessionProvider>
  );
}

const NAV = [
  ["/", "feed"],
  ["/inbox", "inbox"],
  ["/sent", "sent"],
  ["/correspondence", "correspondence"],
] as const;

function Shell() {
  const t = useT();
  const { me, keyStatus, device, lockGeneration } = useSession();
  const path = usePath();
  const recipientIds = useRecipientIds();
  const replyTarget = useReplyTarget();
  const [selection, setSelection] = useState<MessageScope | null>(null);
  const scope = selection?.generation === lockGeneration ? selection : null;
  const [peopleOpen, setPeopleOpen] = useState(() => !matchMedia("(max-width: 760px)").matches);
  const peopleFocusRequested = useRef(false);
  const changePeople = (open: boolean) => { peopleFocusRequested.current = true; setPeopleOpen(open); };
  useEffect(() => {
    if (!peopleFocusRequested.current) return;
    peopleFocusRequested.current = false;
    const selector = peopleOpen ? ".people-heading button" : matchMedia("(max-width: 760px)").matches ? ".people-mobile-toggle" : ".people-open-toggle";
    document.querySelector<HTMLButtonElement>(selector)?.focus();
  }, [peopleOpen]);
  const viewPeople = (next: MessageScope) => { setSelection(next); navigate("/"); };
  // Scheduled mails (PLAN.md 4.28) are written in the language the member last used here.
  const { lang } = useLang();
  const savedLang = useRef(me.settings.mail_lang ?? null);
  useEffect(() => {
    if (savedLang.current === lang) return;
    savedLang.current = lang;
    api.saveSettings({ lang }).catch(() => { savedLang.current = null; });
  }, [lang]);

  // The key is optional (PLAN.md 4.16 K1): an account without one is offered the key step once, on
  // the first page; skipping it leaves a banner pointing to Account -> Keys.
  const noKey = me.pubkey === null;
  const [skipped, setSkipped] = useState(() => keyStepSkipped(me.id));
  const needsKey = noKey && !skipped && path === "/";
  let page: ReactNode;
  if (path === "/restore") page = <Restore />;
  else if (path === "/connect") page = <Connect />;
  else if (needsKey) page = <Onboarding onSkip={() => { skipKeyStep(me.id); setSkipped(true); }} />;
  else if (path === "/keys") page = <Keys />;
  else if (path === "/inbox") page = <Capsules listing="inbox" key="inbox" onViewPerson={viewPeople} />;
  else if (path === "/sent") page = <Capsules listing="sent" key="sent" />;
  else if (path === "/watching") page = <Capsules listing="watching" key="watching" onViewPerson={viewPeople} />;
  else if (path === "/correspondence") page = <Correspondence key="correspondence" onViewPerson={viewPeople} />;
  else if (path === "/try") page = <TryIt />;
  else if (path === "/compose") page = <Compose key={`${recipientIds.join(",")}|${replyTarget?.id ?? ""}`} recipientIds={recipientIds} reply={replyTarget} />;
  else if (path === "/account") page = <Account />;
  else if (path === "/billing") page = <Billing />;
  else if (path === "/contacts") page = <Contacts />;
  else if (path === "/following") page = <Following onView={viewPeople} />;
  else if (path === "/search") page = <Search onViewPerson={viewPeople} onOpen={(listing, id) => { setSelection(null); navigate(listing === "envelopes_sent" || listing === "envelopes_received" ? `/correspondence?tab=${listing === "envelopes_sent" ? "sent" : "received"}&message=${encodeURIComponent(id)}` : `${listing === "feed" ? "/" : `/${listing}`}?message=${encodeURIComponent(id)}`); }} />;
  else if (path === "/admin" && isAdminRole(me.role)) page = <Admin />;
  else page = <Capsules listing="feed" key={`feed:${scope?.memberIds.join(",") ?? "all"}`} scope={scope} onClearScope={() => setSelection(null)} onViewPerson={viewPeople} />;

  return (
    <div className="app-shell">
      <header className="app-nav">
        <a className="brand" href="/" onClick={link("/")}>
          <Logo height={28} title="" />
          Vrelis
        </a>
        {!needsKey && (
          <nav aria-label={t.design.navigation}>
            {NAV.map(([to, label]) => (
              <a key={to} href={to} className={path === to ? "active" : ""} aria-current={path === to ? "page" : undefined} onClick={(e) => { if (to === "/" && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) setSelection(null); link(to)(e); }}>
                <Icon name={label} />
                {t.app.nav[label]}
              </a>
            ))}
          </nav>
        )}
        <div className="nav-actions">
          {!needsKey && (
            <a className="button primary write-link" aria-current={path === "/compose" ? "page" : undefined} href="/compose" onClick={link("/compose")}>
              <Icon name="write" size={18} />
              {t.app.nav.write}
            </a>
          )}
        </div>
        {!needsKey && peopleOpen && <PeoplePanel scope={scope} onView={viewPeople} onClose={() => changePeople(false)} />}
        {!needsKey && !peopleOpen && <button className="people-open-toggle" aria-label={t.design.showPeople} aria-expanded={false} onClick={() => changePeople(true)}><Icon name="folder" size={17} />{t.design.groupsAndPeople}</button>}
        <div className="nav-account">
          {!needsKey && <a href="/following" className={path === "/following" ? "active" : ""} aria-label={t.design.followingTitle} aria-current={path === "/following" ? "page" : undefined} onClick={link("/following")}><Icon name="people" />{t.design.followingTitle}</a>}
          {!needsKey && <button className="people-mobile-toggle icon-button" aria-label={peopleOpen ? t.design.hidePeople : t.design.showPeople} aria-expanded={peopleOpen} aria-controls={peopleOpen ? "people-panel" : undefined} onClick={() => changePeople(!peopleOpen)}><Icon name="folder" /></button>}
          {!needsKey && isAdminRole(me.role) && <a href="/admin" className={path === "/admin" ? "active" : ""} aria-current={path === "/admin" ? "page" : undefined} onClick={link("/admin")}><Icon name="admin" />{t.app.nav.admin}</a>}
          <a className={path === "/account" ? "who active" : "who"} aria-label={`${me.profile?.display_name ?? me.display_name} · ${t.design.profileSettings}`} aria-current={path === "/account" ? "page" : undefined} href="/account" onClick={link("/account")}>
            <span className="avatar" aria-hidden="true">{initials(me.profile?.display_name ?? me.display_name)}</span>
            <span className="account-label"><span className="account-name"><strong>{me.profile?.display_name ?? me.display_name}</strong></span><small>{t.design.profileSettings}</small>{!me.billing && me.usage && <span className="message-budget" title={me.usage.window_seconds === null ? t.design.budgetNoteTotal : t.design.budgetNote} aria-label={t.design.messageBudget(me.usage.messages, me.limits.messages)}>{me.usage.messages}/{me.limits.messages} · {me.usage.window_seconds === null ? t.design.budgetTotal : t.design.budgetWindow}</span>}</span>
          </a>
          {me.billing && (
            <a className="stamp-badge message-budget" href="/billing" aria-label={t.billing.badgeLabel(me.billing.stamps.remaining)} title={t.billing.badgeNote(me.usage?.messages ?? 0, me.limits.messages)} aria-current={path === "/billing" ? "page" : undefined} onClick={link("/billing")}>
              {t.billing.badge(me.billing.stamps.remaining)}
            </a>
          )}
          {!me.billing && me.usage && <span className="mobile-budget message-budget" title={me.usage.window_seconds === null ? t.design.budgetNoteTotal : t.design.budgetNote} aria-label={t.design.messageBudget(me.usage.messages, me.limits.messages)}>{me.usage.messages}/{me.limits.messages} · {me.usage.window_seconds === null ? t.design.budgetTotal : t.design.budgetDay}</span>}
        </div>
      </header>
      <div className="app-workspace">
      {!needsKey && noKey && path !== "/keys" && (
        <div className="banner">
          {t.app.noKey}
          <a href="/keys" onClick={link("/keys")}>{t.app.noKeyLink}</a>.
        </div>
      )}
      {!noKey && (keyStatus === "none" || keyStatus === "mismatch") && path !== "/restore" && path !== "/keys" && (
        <div className="banner">
          {keyStatus === "mismatch" ? t.app.keyMismatch : t.app.keyMissing}
          <a href="/restore" onClick={link("/restore")}>
            {t.app.setUpLink}
          </a>
          .
        </div>
      )}
      {!needsKey && device?.kind === "prf-pin" && (keyStatus === "locked" || keyStatus === "unlocked") && <KeyBar />}
      {!needsKey && <TopSearch />}
      {needsKey && <div className="workspace-refresh"><RefreshButton /></div>}
      <main><SealedNotice path={path} />{page}</main>
      <Footer />
      </div>
    </div>
  );
}

const SKIP_KEY = (memberId: number) => `timecapsule.keyStepSkipped.${memberId}`;
function keyStepSkipped(memberId: number): boolean {
  try { return localStorage.getItem(SKIP_KEY(memberId)) === "1"; } catch { return false; }
}
function skipKeyStep(memberId: number): void {
  try { localStorage.setItem(SKIP_KEY(memberId), "1"); } catch { /* storage blocked: the step shows again next load */ }
}

/** Where members report problems during the pilot (owner, 2026-10-07; to be replaced by a pilot address). */
export const PROBLEM_ADDRESS = "support@vrelis.com";

/** Asked once per page load: is money really moving (reviews/06-action-plan.md 3.6)? A failure reads as "not yet". */
let sellingAnswer: Promise<boolean> | null = null;
function useSellingLive(): boolean {
  const [live, setLive] = useState(false);
  useEffect(() => {
    sellingAnswer ??= api.catalogue().then(sellingLive, () => false);
    let current = true;
    void sellingAnswer.then((v) => current && setLive(v));
    return () => {
      current = false;
    };
  }, []);
  return live;
}

/** Links only (owner, 2026-10-08): theme and language live under Account, or in the public header. */
export function Footer() {
  const t = useT();
  const selling = useSellingLive();
  return (
    <footer className="foot small muted">
      <a href="/guide" onClick={link("/guide")}>
        {t.footer.guide}
      </a>
      <a href="/developers" onClick={link("/developers")}>
        {t.footer.developers}
      </a>
      <a href="/pricing" onClick={link("/pricing")}>{t.footer.pricing}</a>
      <a href="/terms" onClick={link("/terms")}>
        {t.footer.terms}
      </a>
      <a href="/privacy" onClick={link("/privacy")}>{t.footer.privacy}</a>
      <a href="/cookies" onClick={link("/cookies")}>{t.footer.cookies}</a>
      <a href="/contact" onClick={link("/contact")}>{t.footer.contact}</a>
      <a href={`mailto:${PROBLEM_ADDRESS}?subject=${encodeURIComponent(t.footer.reportSubject)}`}>{t.footer.report}</a>
      <span className="foot-brand">
        <span className="foot-note">{selling ? t.legal.sellingLive : t.legal.freePilot}</span>
        <span className="foot-operator">
          <img src="/brand/perikle-mark.png" alt="" width={23} height={16} />
          {t.footer.operator}
        </span>
        <span className="foot-ai" title={t.footer.aiAssistedTitle}>
          <img src="/brand/ai-assisted.png" alt="" width={16} height={16} />
          {t.footer.aiAssisted}
        </span>
      </span>
    </footer>
  );
}

/** A native dropdown stays usable with thirteen languages, keyboards and screen readers. */
export function LanguageSelect() {
  const t = useT();
  const { lang, setLang } = useLang();
  if (LANGS.length < 2) return null;
  return (
    <select className="language-select" aria-label={t.footer.language} value={lang} onChange={(event) => setLang(event.target.value as typeof lang)} dir="auto">
      {LANGS.map((l) => (
        <option key={l} value={l} lang={LOCALES[l].htmlLang} dir={LOCALES[l].dir}>
          {langName(l)}
        </option>
      ))}
    </select>
  );
}

/** Theme and language (Account): the only place a signed-in member changes them. */
export function Preferences() {
  const t = useT();
  const { lang } = useLang();
  const [theme, setTheme] = useTheme();
  return (
    <section className="card">
      <h2>{t.account.preferences}</h2>
      <p className="small muted">{t.account.preferencesNote}</p>
      <div className="pref-row">
        <span className="pref-label">{t.footer.theme}</span>
        <span className="chips" role="group" aria-label={t.footer.theme}>
          {THEMES.map((th) => (
            <button key={th} type="button" className={th === theme ? "chip on" : "chip"} aria-pressed={th === theme} onClick={() => setTheme(th)}>
              {t.footer.themes[th]}
            </button>
          ))}
        </span>
      </div>
      {LANGS.length > 1 && (
        <div className="pref-row">
          <span className="pref-label">{t.footer.language}</span>
          <LanguageSelect />
        </div>
      )}
      {lang !== "en" && lang !== "sr" && <p className="small muted">{t.footer.translationNote}</p>}
    </section>
  );
}

/** Header of the pages anyone can open (sign-in, mail links, the guide): the brand and the language. */
export function PublicHeader() {
  const t = useT();
  const path = usePath();
  return (
    <header className="top public">
      <a className="brand" href="/" onClick={link("/")}>
        <Logo height={20} title="" />
        Vrelis
      </a>
      <a className="pilot-badge" href="/#pilot" title={t.pilot.badgeTitle}>
        {t.pilot.badge}
      </a>
      <span className="right">
        <RefreshButton reloadPage={path === "/guide" || path === "/terms" || path === "/privacy" || path === "/cookies" || path === "/contact"} />
        <LanguageSelect />
      </span>
    </header>
  );
}

/** Click handler for in-app links: keeps normal browser behaviour for modified clicks. */
export function link(to: string) {
  return (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to);
  };
}
